/**
 * Apply Reporting Flag Logic to Invoicing
 * 
 * Frontend utility to filter locked sections from dropdown data.
 * When a section has lock_flag=true, child Tasks/Sections are hidden from selection.
 * Only the flagged Section appears in the dropdown/accordion.
 * 
 * @module filter-locked-sections
 */

import _ from 'lodash';

/**
 * Filters locked sections from the hierarchy for invoice task selection.
 * When a section is locked (lock_flag=true), it replaces all its children as a single selectable item.
 * 
 * @param {Array} sections - Array of section/stage objects from API
 * @param {Object} options - Configuration options
 * @param {Array} options.alwaysIncludeTaskIds - Task IDs to always include (for edit mode/historical data)
 * @returns {Array} Filtered sections for dropdown/accordion display
 * 
 * @example
 * // Basic usage - hide all children of locked sections
 * const filtered = filterLockedSections(stages);
 * 
 * @example
 * // Edit mode - preserve historical task assignments
 * const filtered = filterLockedSections(stages, { 
 *   alwaysIncludeTaskIds: existingTaskIds 
 * });
 */
export const filterLockedSections = (sections, options = {}) => {
    const { alwaysIncludeTaskIds = [] } = options;

    if (!Array.isArray(sections)) return [];

    return sections.map(section => {
        const sectionCopy = _.cloneDeep(section);

        // If section is locked, return it as a selectable item without children (unless historical)
        if (sectionCopy.lock_flag === true) {
            // Check if any of the section's tasks or nested tasks are in the historical list
            const hasHistoricalTasks = checkForHistoricalTasks(sectionCopy, alwaysIncludeTaskIds);

            if (hasHistoricalTasks && alwaysIncludeTaskIds.length > 0) {
                // Historical mode - preserve the structure for viewing but mark as locked
                return {
                    ...sectionCopy,
                    isLockedSection: true,
                    isHistoricalView: true,
                    // Keep nested stages/tasks for historical viewing
                    stages: sectionCopy.stages
                        ? filterLockedSections(sectionCopy.stages, options)
                        : [],
                    tasks: sectionCopy.tasks || []
                };
            }

            // Normal locked behavior - hide all children
            return {
                ...sectionCopy,
                stages: [],
                tasks: [],
                isLockedSection: true
            };
        }

        // Not locked - recursively process child stages
        return {
            ...sectionCopy,
            stages: sectionCopy.stages
                ? filterLockedSections(sectionCopy.stages, options)
                : [],
            tasks: sectionCopy.tasks || []
        };
    });
};

/**
 * Checks if a section or its children contain any historical task IDs.
 * Used to determine if we should preserve the structure for historical viewing.
 * 
 * @param {Object} section - The section to check
 * @param {Array} historicalTaskIds - Array of task IDs to look for
 * @returns {boolean} True if any historical tasks are found
 */
const checkForHistoricalTasks = (section, historicalTaskIds) => {
    if (!historicalTaskIds || historicalTaskIds.length === 0) return false;

    // Check direct tasks
    if (section.tasks && Array.isArray(section.tasks)) {
        const hasDirectMatch = section.tasks.some(task =>
            historicalTaskIds.includes(task.id)
        );
        if (hasDirectMatch) return true;
    }

    // Recursively check nested stages
    if (section.stages && Array.isArray(section.stages)) {
        return section.stages.some(nestedStage =>
            checkForHistoricalTasks(nestedStage, historicalTaskIds)
        );
    }

    return false;
};

/**
 * Extracts task IDs from an existing progress claim for edit mode.
 * These IDs should always be included in dropdowns to preserve historical data.
 * 
 * @param {Object} progressClaim - The progress claim object
 * @returns {Array} Array of task IDs
 */
export const extractExistingTaskIds = (progressClaim) => {
    if (!progressClaim?.progressClaimTask) return [];
    return progressClaim.progressClaimTask.map(t => t.taskId);
};

/**
 * Filters the selectedTask array to respect lock_flag.
 * When a section is locked, tasks under it are aggregated to the section level.
 * 
 * @param {Array} selectedTask - Array of tasks with allocation data
 * @param {Array} stages - Array of stage/section objects with hierarchy
 * @param {Array} alwaysIncludeTaskIds - Task IDs from historical claims to always include
 * @returns {Array} Filtered selectedTask array
 */
export const filterSelectedTasksByLockFlag = (selectedTask, stages, alwaysIncludeTaskIds = []) => {
    if (!Array.isArray(selectedTask)) return [];
    if (!Array.isArray(stages)) return selectedTask;

    // Build a map of locked section IDs and their descendant task IDs
    const lockedSectionTaskMap = new Map();

    const buildLockedMap = (sections, parentLockedId = null) => {
        sections.forEach(section => {
            const currentLockedId = section.lock_flag ? section.id : parentLockedId;

            // If we're inside a locked section, map all tasks to that section
            if (currentLockedId) {
                if (section.tasks && Array.isArray(section.tasks)) {
                    section.tasks.forEach(task => {
                        // Only map if not a historical task
                        if (!alwaysIncludeTaskIds.includes(task.id)) {
                            lockedSectionTaskMap.set(task.id, currentLockedId);
                        }
                    });
                }
            }

            // Process nested stages
            if (section.stages && Array.isArray(section.stages)) {
                buildLockedMap(section.stages, currentLockedId);
            }
        });
    };

    buildLockedMap(stages);

    // Filter tasks that are under locked sections (unless historical)
    return selectedTask.filter(task => {
        // Always include historical tasks
        if (alwaysIncludeTaskIds.includes(task.id)) return true;
        // Exclude tasks that are mapped to a locked section
        return !lockedSectionTaskMap.has(task.id);
    });
};

const filterLockedSectionsUtil = {
    filterLockedSections,
    extractExistingTaskIds,
    filterSelectedTasksByLockFlag,
};

export default filterLockedSectionsUtil;

